import { useNotificationToast } from '@context/NotificationToastContext'
import {
  IClinicNumber,
  IPortDetailsStep2,
  ITwilioPortRequest,
} from '@dataTypes/clinic.type'
import { checkFaxPortability } from '@services/api/fax/numbers.api'
import {
  resubmitFaxPortRequestDbAction,
  submitFaxPortRequestDbAction,
} from '@store/actions/settings-communication.action'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import {
  formatPhoneNumber,
  normalizePhoneNumber,
} from '@utils/numberFormatter.util'
import { toPortRequestClinicSnapshot } from '@utils/portRequestPayload.util'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'

const findNonPortable = (portability: unknown): boolean => {
  if (!Array.isArray(portability)) return false
  return portability.some((entry) => {
    if (!entry || typeof entry !== 'object') return false
    const e = entry as Record<string, unknown>
    return (
      e.portable === false ||
      e.notPortable === true ||
      e.status === 'NOT_PORTABLE'
    )
  })
}

const defaultNumberData: IClinicNumber = {
  clinic_number_id: '',
  clinic_id: '',
  is_verified: false,
  is_portable: false,
  phone_number: '',
  phone_carrier: '',
  number_sid: '',
  status: '',
  purchased_date: '',
  released_date: '',
  released_reason: '',
}

const defaultDetailsData: IPortDetailsStep2 = {
  full_name: '',
  email_address: '',
  phone_number: '',
  wireless_account_number: '',
  pin_or_ssn: '',
  billing_statement_file: null,
}

interface IPortFaxHooks {
  busy: boolean
  detailsData: IPortDetailsStep2
  handleClose: () => void
  handleDetailsChange: (field: string, value: string) => void
  handleNext: () => Promise<void>
  handleNumberChange: (field: string, value: string | boolean | null) => void
  handlePrevious: () => void
  handleProceedSave: () => Promise<void>
  isResubmitMode: boolean
  numberData: IClinicNumber
  resubmitRequest: ITwilioPortRequest | null
  step: number
  steps: string[]
  validationErrors: { [key: string]: string }
}

type RunAsyncFn = <T>(
  operation: () => Promise<T>,
  messages: {
    successTitle?: string
    successMessage?: string
    errorTitle: string
    errorFallbackMessage: string
  },
) => Promise<T | undefined>

type Props = {
  show: boolean
  clinicId: string
  onHide: () => void
  onAdded: () => void
  resubmitRequest?: ITwilioPortRequest | null
  runAsync?: RunAsyncFn
  loadingContainer?: string
}

const DEFAULT_FAX_LOADING_CONTAINER = '.scp-fax-container'

const usePortFax = ({
  show,
  clinicId,
  onHide,
  onAdded,
  resubmitRequest = null,
  runAsync,
  loadingContainer = DEFAULT_FAX_LOADING_CONTAINER,
}: Props): IPortFaxHooks => {
  const { showToast } = useNotificationToast()
  const dispatch = useAppDispatch()
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const { showLoading, hideLoading } = useGlobalLoadingActions()

  const isResubmitMode = !!resubmitRequest
  const [step, setStep] = useState(0)
  const [busy, setBusy] = useState(false)
  const [numberData, setNumberData] = useState<IClinicNumber>(defaultNumberData)
  const [detailsData, setDetailsData] =
    useState<IPortDetailsStep2>(defaultDetailsData)
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string
  }>({})

  const steps = useMemo(
    () =>
      isResubmitMode
        ? ['Update number', 'Update details', 'Review & resubmit']
        : ['Select number', 'Enter details', 'Review info'],
    [isResubmitMode],
  )

  const reset = (): void => {
    setStep(0)
    setBusy(false)
    setNumberData(defaultNumberData)
    setDetailsData(defaultDetailsData)
    setValidationErrors({})
  }

  useEffect(() => {
    if (!show) return
    setStep(0)
    setValidationErrors({})

    if (resubmitRequest) {
      const numbers = (resubmitRequest.phone_numbers || []).map((number) =>
        formatPhoneNumber(normalizePhoneNumber(number), false),
      )
      setNumberData({
        ...defaultNumberData,
        clinic_id: resubmitRequest.clinic_id,
        phone_number: numbers.join('\n'),
        phone_carrier: resubmitRequest.phone_carrier || '',
        is_portable: true,
      })
      setDetailsData({
        full_name: resubmitRequest.representative_name || '',
        email_address: resubmitRequest.representative_email || '',
        phone_number: '',
        wireless_account_number: resubmitRequest.wireless_account_number || '',
        pin_or_ssn: resubmitRequest.wireless_pin_or_ssn || '',
        billing_statement_file: null,
      })
      return
    }

    setNumberData(defaultNumberData)
    setDetailsData(defaultDetailsData)
  }, [show, resubmitRequest])

  const handleClose = (): void => {
    reset()
    onHide()
  }

  const isValidPhoneNumber = (value: string): boolean => {
    const normalized = value.replace(/\D/g, '')
    return (
      normalized.length === 10 ||
      (normalized.length === 11 && normalized.startsWith('1'))
    )
  }

  const parseNumbers = useCallback(
    (): string[] =>
      numberData.phone_number
        .split('\n')
        .map((item) => normalizePhoneNumber(item.trim()))
        .filter((item) => item !== ''),
    [numberData.phone_number],
  )

  const validateForm = useCallback(async (): Promise<boolean> => {
    const errors: { [key: string]: string } = {}
    if (step === 0) {
      const numbers = parseNumbers()
      if (numbers.length === 0) {
        errors.phone_number = 'Input phone number'
      } else if (numbers.some((item) => !isValidPhoneNumber(item))) {
        errors.phone_number = 'Invalid phone number'
      }
      if (!numberData.phone_carrier?.trim()) {
        errors.phone_carrier = 'Select phone carrier'
      }
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors)
        return false
      }
      setBusy(true)
      try {
        const res = await checkFaxPortability(numbers)
        if (res.statusCode !== 200) {
          setValidationErrors({
            is_portable: res.message || 'Failed to check portability.',
          })
          return false
        }
        if (findNonPortable(res.data?.portability)) {
          setValidationErrors({
            is_portable:
              'One or more numbers cannot be ported. Remove them or enter a different number.',
          })
          return false
        }
        setNumberData((prev) => ({ ...prev, is_portable: true }))
      } catch {
        setValidationErrors({ is_portable: 'Failed to check portability.' })
        return false
      } finally {
        setBusy(false)
      }
    }
    if (step === 1) {
      if (!detailsData.full_name?.trim()) {
        errors.full_name = 'Authorizing person is required'
      }
      if (!detailsData.email_address?.trim()) {
        errors.email_address = 'Email address is required'
      }
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors)
        return false
      }
    }
    setValidationErrors({})
    return true
  }, [
    step,
    parseNumbers,
    numberData.phone_carrier,
    detailsData.full_name,
    detailsData.email_address,
  ])

  const handleNumberChange = (
    field: string,
    value: string | boolean | null,
  ): void => {
    const updated: Partial<IClinicNumber> = { [field]: value }
    if (field === 'phone_number') {
      updated.is_portable = false
      setValidationErrors((prev) => {
        if (!prev.is_portable && !prev.phone_number) return prev
        const next = { ...prev }
        delete next.is_portable
        delete next.phone_number
        return next
      })
    }
    setNumberData((prev) => ({ ...prev, ...updated }))
  }

  const handleDetailsChange = (field: string, value: string): void => {
    setValidationErrors((prev) => {
      if (!prev[field]) return prev
      const next = { ...prev }
      delete next[field]
      return next
    })
    setDetailsData((prev) => ({ ...prev, [field]: value }))
  }

  const handleNext = async (): Promise<void> => {
    if (await validateForm()) setStep((prev) => prev + 1)
  }

  const handlePrevious = (): void => setStep((prev) => prev - 1)

  const submitNewFaxPort = async (
    numbers: string[],
    clinicSnapshot: NonNullable<ReturnType<typeof toPortRequestClinicSnapshot>>,
  ): Promise<void> => {
    const submit = (): Promise<ITwilioPortRequest> =>
      dispatch(
        submitFaxPortRequestDbAction({
          clinicId,
          clinicSnapshot,
          phone_numbers: numbers,
          phone_carrier: numberData.phone_carrier || '',
          representative_name: detailsData.full_name?.trim() || '',
          representative_email: detailsData.email_address?.trim() || '',
          wireless_account_number:
            detailsData.wireless_account_number?.trim() || null,
          wireless_pin_or_ssn: detailsData.pin_or_ssn?.trim() || null,
        }),
      ).unwrap()

    if (runAsync) {
      const result = await runAsync(submit, {
        successTitle: 'Port request submitted',
        successMessage: `${numbers.length} fax number(s) submitted for review.`,
        errorTitle: 'Fax numbers',
        errorFallbackMessage: 'Failed to submit fax port request.',
      })
      if (result !== undefined) {
        onAdded()
        handleClose()
      }
      return
    }

    setBusy(true)
    showLoading({ container: loadingContainer })
    try {
      await submit()
      showToast(
        createSuccessToast(
          'Port request submitted',
          `${numbers.length} fax number(s) submitted for review.`,
        ),
      )
      onAdded()
      handleClose()
    } catch (error) {
      showToast(
        createFailedToast(
          'Fax numbers',
          error instanceof Error
            ? error.message
            : 'Failed to submit fax port request.',
        ),
      )
    } finally {
      hideLoading(loadingContainer)
      setBusy(false)
    }
  }

  const resubmitFaxPort = async (
    numbers: string[],
    clinicSnapshot: NonNullable<ReturnType<typeof toPortRequestClinicSnapshot>>,
  ): Promise<void> => {
    if (!resubmitRequest?.twilio_port_request_id) return

    const submit = (): Promise<ITwilioPortRequest> =>
      dispatch(
        resubmitFaxPortRequestDbAction({
          portRequestId: resubmitRequest.twilio_port_request_id!,
          clinicId,
          clinicSnapshot,
          phone_numbers: numbers,
          phone_carrier: numberData.phone_carrier || '',
          representative_name: detailsData.full_name?.trim() || '',
          representative_email: detailsData.email_address?.trim() || '',
          wireless_account_number:
            detailsData.wireless_account_number?.trim() || null,
          wireless_pin_or_ssn: detailsData.pin_or_ssn?.trim() || null,
        }),
      ).unwrap()

    if (runAsync) {
      const result = await runAsync(submit, {
        successTitle: 'Fax port request resubmitted',
        successMessage:
          'Your corrected fax port request was sent back for review.',
        errorTitle: 'Could not resubmit fax port request',
        errorFallbackMessage: 'Failed to resubmit fax port request.',
      })
      if (result !== undefined) {
        handleClose()
      }
      return
    }

    setBusy(true)
    showLoading({ container: loadingContainer })
    try {
      await submit()
      showToast(
        createSuccessToast(
          'Fax port request resubmitted',
          'Your corrected fax port request was sent back for review.',
        ),
      )
      handleClose()
    } catch (error) {
      showToast(
        createFailedToast(
          'Fax numbers',
          error instanceof Error
            ? error.message
            : 'Failed to resubmit fax port request.',
        ),
      )
    } finally {
      hideLoading(loadingContainer)
      setBusy(false)
    }
  }

  const handleProceedSave = async (): Promise<void> => {
    const numbers = parseNumbers()
    const clinicSnapshot = toPortRequestClinicSnapshot(selectedClinic)
    if (!clinicId || numbers.length === 0) {
      setValidationErrors((prev) => ({
        ...prev,
        is_portable: 'Missing clinic or phone number.',
      }))
      return
    }
    if (!clinicSnapshot) {
      setValidationErrors((prev) => ({
        ...prev,
        is_portable:
          'Clinic address details are required. Update clinic settings and try again.',
      }))
      return
    }

    if (isResubmitMode) {
      await resubmitFaxPort(numbers, clinicSnapshot)
      return
    }

    await submitNewFaxPort(numbers, clinicSnapshot)
  }

  return {
    busy,
    detailsData,
    handleClose,
    handleDetailsChange,
    handleNext,
    handleNumberChange,
    handlePrevious,
    handleProceedSave,
    isResubmitMode,
    numberData,
    resubmitRequest,
    step,
    steps,
    validationErrors,
  }
}

export default usePortFax
